import { useState } from 'react'
import { ChevronDown, ChevronUp, History, Loader2 } from 'lucide-react'
import { useLotAudit, useNodeAudit, useZoneAudit } from '@/hooks/useStorage'
import { actorLabel, describeStorageAuditEvent } from '@/lib/storageAudit'
import type { ResolvedActor } from '@/types/storage'

/** What a row needs, whether it came from a node, a lot or a zone trail. */
type RenderableEntry = {
  id: string
  eventType: string
  oldValue: unknown
  newValue: unknown
  reason: string | null
  changedBy: string
  changedByActor: ResolvedActor
  changedAt: string
}

/**
 * The audit trail for a node or a lot.
 *
 * Collapsed by default and only fetched once opened — a trail grows a row per
 * put-away forever, and nobody browsing shelves wants that on every click.
 *
 * Deliberately NOT a table of raw old/new JSON, which is what the forms-side
 * `AuditTrailPanel` shows. That panel reports single field edits where the
 * before and after are short strings; here an event's payload is a whole node
 * row, and rendering it would put `{"gridRows":3,"labelScheme":...}` in front
 * of somebody trying to find out who emptied a slot.
 */

type Common = {
  title?: string
  /**
   * Start expanded. Set where the panel is itself revealed on demand — a lot's
   * history is rendered only after the operator asks for it, and making them
   * click a second disclosure to see what they just requested is a dead click.
   */
  defaultOpen?: boolean
}

type Props =
  | ({ nodeId: string; lotId?: never; zoneId?: never; subtree?: boolean } & Common)
  | ({ lotId: string; nodeId?: never; zoneId?: never; subtree?: never } & Common)
  | ({ zoneId: string; nodeId?: never; lotId?: never; subtree?: never } & Common)

export function StorageAuditPanel(props: Props) {
  const [open, setOpen] = useState(props.defaultOpen ?? false)
  const isLot = 'lotId' in props && !!props.lotId
  const isZone = 'zoneId' in props && !!props.zoneId

  // All three hooks are called unconditionally — hook order cannot depend on
  // which variant of the props union this is. The unused ones are disabled by a
  // null id and never fetch.
  const nodeQuery = useNodeAudit(
    isLot || isZone ? null : props.nodeId,
    open,
    'subtree' in props ? Boolean(props.subtree) : false,
  )
  const lotQuery = useLotAudit(isLot ? props.lotId : null, open)
  const zoneQuery = useZoneAudit(isZone ? props.zoneId : null, open)
  const query = isLot ? lotQuery : isZone ? zoneQuery : nodeQuery

  const entries = query.data?.items ?? []
  const total = query.data?.total ?? 0
  const title = props.title ?? 'History'

  return (
    <section className='rounded-lg border'>
      <button
        type='button'
        className='flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-muted/50'
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span className='flex items-center gap-2 text-sm font-medium'>
          <History className='h-4 w-4 text-muted-foreground' />
          {title}
          {open && !query.isLoading && (
            <span className='text-xs font-normal text-muted-foreground'>{total}</span>
          )}
        </span>
        <span className='flex items-center gap-2 text-muted-foreground'>
          {open && query.isFetching && <Loader2 className='h-3.5 w-3.5 animate-spin' />}
          {open ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
        </span>
      </button>

      {open && (
        <div className='border-t'>
          {query.isLoading ? (
            <p className='px-3 py-6 text-center text-xs text-muted-foreground'>Loading history…</p>
          ) : query.error ? (
            <p className='px-3 py-4 text-xs text-destructive'>
              {query.error instanceof Error ? query.error.message : 'Could not load the history.'}
            </p>
          ) : entries.length === 0 ? (
            /* An empty trail is not the same claim as "nothing ever happened":
               stock loaded by the seed or an import never went through the
               put-away path, so it legitimately has no recorded history. Saying
               so stops an empty panel reading as a bug. */
            <p className='px-3 py-5 text-center text-xs leading-relaxed text-muted-foreground'>
              No recorded history.
              <br />
              {isLot
                ? 'Only stock moved through the app is tracked.'
                : isZone
                  ? 'Nothing has been recorded against this zone yet.'
                  : 'Nothing has been recorded against this location yet.'}
            </p>
          ) : (
            <>
              <ol className='divide-y'>
                {entries.map((entry) => (
                  <AuditRow key={entry.id} entry={entry} />
                ))}
              </ol>
              {total > entries.length && (
                <p className='border-t px-3 py-2 text-center text-[11px] text-muted-foreground'>
                  Showing the {entries.length} most recent of {total}.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  )
}

function AuditRow({ entry }: { entry: RenderableEntry }) {
  const { label, detail } = describeStorageAuditEvent(entry)
  const at = new Date(entry.changedAt)
  const named = Boolean(entry.changedByActor?.name)

  return (
    <li className='space-y-0.5 px-3 py-2'>
      <div className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5'>
        <span className='text-sm font-medium leading-snug'>{label}</span>
        {detail && <span className='text-xs text-muted-foreground'>{detail}</span>}
      </div>

      <div className='flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground'>
        {/* The full timestamp on hover: a trail is read to establish a sequence,
            and "18:42" is ambiguous across days. */}
        <time dateTime={entry.changedAt} title={at.toLocaleString()}>
          {at.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </time>
        <span aria-hidden>·</span>
        {/* The person, where the sub resolves to one. The raw sub stays on the
            tooltip — it is the actual record, and the name is a lookup that can
            change under it. Monospace only when we are showing the id. */}
        <span
          className={named ? undefined : 'font-mono'}
          title={
            entry.changedByActor?.email
              ? `${entry.changedByActor.email} · ${entry.changedBy}`
              : entry.changedBy
          }
        >
          {actorLabel(entry)}
        </span>
      </div>

      {entry.reason && (
        <p className='pt-0.5 text-[11px] italic leading-snug text-muted-foreground'>
          Reason: {entry.reason}
        </p>
      )}
    </li>
  )
}
